import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Upload, Hash } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import CryptoJS from 'crypto-js';

interface HashResult {
  algorithm: string;
  hash: string;
  length: number;
}

export const HashGeneratorTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState<HashResult[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const algorithms = [
    { name: 'MD5', func: CryptoJS.MD5 },
    { name: 'SHA-1', func: CryptoJS.SHA1 },
    { name: 'SHA-256', func: CryptoJS.SHA256 },
    { name: 'SHA-512', func: CryptoJS.SHA512 },
    { name: 'SHA-224', func: CryptoJS.SHA224 },
    { name: 'SHA-384', func: CryptoJS.SHA384 },
    { name: 'SHA-3', func: CryptoJS.SHA3 },
  ];

  const generateHashes = (text: string) => {
    if (!text) {
      setHashes([]);
      return;
    }

    const results: HashResult[] = algorithms.map(({ name, func }) => {
      const hash = func(text).toString();
      return {
        algorithm: name,
        hash,
        length: hash.length,
      };
    });

    setHashes(results);
  };

  useEffect(() => {
    generateHashes(input);
  }, [input]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setHashes([]);
  };

  const sampleTexts = [
    'Hello, World!',
    'The quick brown fox jumps over the lazy dog',
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
    '12345',
    'password123',
  ];

  const loadSample = (text: string) => {
    setInput(text);
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Hash className="w-5 h-5" />
            <span>{t('tools.hashGenerator.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={clearAll}
                variant="outline"
              >
                {t('common.clear')}
              </Button>
            </div>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.json,.xml,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="mb-2"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('common.selectFile')}
              </Button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.hashGenerator.sampleTexts')}:
              </label>
              <div className="flex flex-wrap gap-2">
                {sampleTexts.map((text, index) => (
                  <Button
                    key={index}
                    onClick={() => loadSample(text)}
                    variant="outline"
                    size="sm"
                  >
                    {text.length > 20 ? `${text.substring(0, 20)}...` : text}
                  </Button>
                ))}
              </div>
            </div>

            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('tools.hashGenerator.input')}
              className="min-h-[150px] font-mono text-sm"
            />

            {input && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Input: {input.length} characters, {new Blob([input]).size} bytes
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Hash Results */}
      {hashes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.hashGenerator.hashResults')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {hashes.map((result) => (
                <div
                  key={result.algorithm}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {result.algorithm}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({result.length} {t('tools.hashGenerator.chars')})
                      </span>
                    </div>
                    <Button
                      onClick={() => copyToClipboard(result.hash)}
                      size="sm"
                      variant="outline"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      {t('common.copy')}
                    </Button>
                  </div>
                  <div className="font-mono text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded border break-all">
                    {result.hash}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hash Comparison */}
      {hashes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.hashGenerator.hashComparison')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 font-medium text-gray-900 dark:text-gray-100">
                      {t('tools.hashGenerator.algorithm')}
                    </th>
                    <th className="text-left py-2 font-medium text-gray-900 dark:text-gray-100">
                      {t('tools.hashGenerator.length')}
                    </th>
                    <th className="text-left py-2 font-medium text-gray-900 dark:text-gray-100">
                      {t('tools.hashGenerator.security')}
                    </th>
                    <th className="text-left py-2 font-medium text-gray-900 dark:text-gray-100">
                      {t('tools.hashGenerator.useCase')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'MD5', length: '128-bit', security: 'Weak', useCase: 'Checksums (not secure)' },
                    { name: 'SHA-1', length: '160-bit', security: 'Weak', useCase: 'Legacy systems' },
                    { name: 'SHA-224', length: '224-bit', security: 'Good', useCase: 'General purpose' },
                    { name: 'SHA-256', length: '256-bit', security: 'Strong', useCase: 'Cryptographic applications' },
                    { name: 'SHA-384', length: '384-bit', security: 'Strong', useCase: 'High security' },
                    { name: 'SHA-512', length: '512-bit', security: 'Strong', useCase: 'Maximum security' },
                    { name: 'SHA-3', length: '256-bit', security: 'Strong', useCase: 'Latest standard' },
                  ].map((info) => (
                    <tr key={info.name} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 font-mono">{info.name}</td>
                      <td className="py-2">{info.length}</td>
                      <td className="py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          info.security === 'Strong' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : info.security === 'Good'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {info.security}
                        </span>
                      </td>
                      <td className="py-2 text-gray-600 dark:text-gray-400">{info.useCase}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.hashGenerator.aboutHashFunctions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">What are Hash Functions?</h4>
              <p>
                Hash functions are mathematical algorithms that convert input data of any size into a fixed-size string of characters. 
                They are designed to be one-way functions, meaning it's computationally infeasible to reverse the process.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Common Uses</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Password storage (with salt)</li>
                <li>Data integrity verification</li>
                <li>Digital signatures</li>
                <li>Blockchain and cryptocurrency</li>
                <li>File checksums</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Security Recommendations</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Use SHA-256 or higher for new applications</li>
                <li>Avoid MD5 and SHA-1 for security-critical applications</li>
                <li>Always use salt when hashing passwords</li>
                <li>Consider using specialized password hashing functions like bcrypt or Argon2</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
